/**
 * Organization configurations for multi-tenant deployment.
 *
 * Each org config defines the branding, domain names, department names,
 * and system references that make the experiment feel like it's coming
 * from the participant's actual organization.
 *
 * URL parameter selects the org: ?org=colostate, ?org=uccs
 * Default (no parameter) uses 'uccs'.
 *
 * IMPORTANT: When adding a new org:
 *   1. Add the config object below
 *   2. Add it to the ORG_CONFIGS map
 *   3. Define fakeDomains for phishing emails (must NOT match the real domain)
 *   4. Test that congruence manipulation still works (legitimate emails use real domain,
 *      phishing emails use fake lookalike domains)
 */

export interface OrgConfig {
  orgId: string;
  orgName: string;
  orgShortName: string;

  // Email domains
  emailDomain: string;           // Real org domain (used for legitimate emails)
  internalDomains: string[];     // All real subdomains participants would recognize
  fakeDomains: {                 // Fake lookalike domains for phishing emails
    it: string;                  // Fake IT domain (e.g., "colostate-it.org")
    hr: string;                  // Fake HR domain
    finance: string;             // Fake finance domain (company-invoicing.com)
    financeOrg: string;          // Fake finance .org domain (company-finance.org)
    surveys: string;             // Fake survey domain (company-surveys.com)
    notifications: string;       // Fake notification domain (company-notifications.net)
    security: string;            // Fake security domain (company-security.com)
    exec: string;                // Fake exec domain (company-exec.net)
    learn: string;               // Fake learning domain (company-learn.org)
    mail: string;                // Fake mail domain (company-mail.net)
    support: string;             // Fake support domain (company-support.net)
  };

  // Department names (as participants would recognize them)
  itDepartment: string;
  itDepartmentFull: string;
  hrDepartment: string;
  financeDepartment: string;
  executiveTitle: string;
  executiveOffice: string;
  complianceDepartment: string;

  // Infrastructure
  wifiNetwork: string;
  wifiVisitor: string;
  vpnDomain: string;
  sharepointDomain: string;
  intranetDomain: string;
  trainingDomain: string;
  mfaSystem: string;
  mfaApp: string;
  emailSystem: string;
  lms: string;
  studentPortal: string;

  // Location
  location: string;
  locationType: string; // "campus" or "office"

  // Tasks
  enabledTasks: ('email' | 'device')[];

  // 'sona' orgs need a SONA code in the entry URL: without it no credit can be granted.
  recruitment?: 'sona';
}

// ════════════════════════════════════════════════════════════════════════
// Colorado State University, Fort Collins
// ════════════════════════════════════════════════════════════════════════
const COLOSTATE: OrgConfig = {
  orgId: 'colostate',
  orgName: 'Colorado State University',
  orgShortName: 'CSU',

  emailDomain: 'colostate.edu',
  internalDomains: [
    'colostate.edu',
    'sharepoint.colostate.edu',
    'training.colostate.edu',
    'id.colostate.edu',
    'intranet.colostate.edu',
    'hr.colostate.edu',
    'canvas.colostate.edu',
  ],
  fakeDomains: {
    it: 'colostate-it.org',
    hr: 'csu-hr.net',
    finance: 'colostate-invoicing.com',
    financeOrg: 'colostate-finance.org',
    surveys: 'csu-surveys.com',
    notifications: 'colostate-notifications.net',
    security: 'csu-security.com',
    exec: 'colostate-exec.net',
    learn: 'csu-learn.org',
    mail: 'colostate-mail.net',
    support: 'csu-support.net',
  },

  itDepartment: 'ACNS',
  itDepartmentFull: 'Academic Computing and Network Services (ACNS)',
  hrDepartment: 'Human Resources',
  financeDepartment: 'Financial Services',
  executiveTitle: 'President',
  executiveOffice: 'Office of the President',
  complianceDepartment: 'Compliance Office',

  wifiNetwork: 'csu-net',
  wifiVisitor: 'csu-visitor',
  vpnDomain: 'vpn.colostate.edu',
  sharepointDomain: 'sharepoint.colostate.edu',
  intranetDomain: 'intranet.colostate.edu',
  trainingDomain: 'training.colostate.edu',
  mfaSystem: 'Duo',
  mfaApp: 'Duo Mobile',
  emailSystem: 'Microsoft 365',
  lms: 'Canvas',
  studentPortal: 'RAMweb',

  location: 'Fort Collins, CO',
  locationType: 'campus',

  enabledTasks: ['email', 'device'],
  recruitment: 'sona',
};

// ════════════════════════════════════════════════════════════════════════
// University of Colorado Colorado Springs
// ════════════════════════════════════════════════════════════════════════
const UCCS: OrgConfig = {
  orgId: 'uccs',
  orgName: 'University of Colorado Colorado Springs',
  orgShortName: 'UCCS',

  emailDomain: 'uccs.edu',
  internalDomains: [
    'uccs.edu',
    'sharepoint.uccs.edu',
    'training.uccs.edu',
    'id.uccs.edu',
    'intranet.uccs.edu',
    'hr.uccs.edu',
  ],
  fakeDomains: {
    it: 'uccs-it.org',
    hr: 'uccs-hr.net',
    finance: 'uccs-invoicing.com',
    financeOrg: 'uccs-finance.org',
    surveys: 'uccs-surveys.com',
    notifications: 'uccs-notifications.net',
    security: 'uccs-security.com',
    exec: 'uccs-exec.net',
    learn: 'uccs-learn.org',
    mail: 'uccs-mail.net',
    support: 'uccs-support.net',
  },

  itDepartment: 'OIT',
  itDepartmentFull: 'Office of Information Technology (OIT)',
  hrDepartment: 'Human Resources',
  financeDepartment: 'Financial Services',
  executiveTitle: 'Chancellor',
  executiveOffice: 'Chancellor\'s Office',
  complianceDepartment: 'Compliance Office',

  wifiNetwork: 'UCCS-Wireless',
  wifiVisitor: 'UCCS-Guest',
  vpnDomain: 'vpn.uccs.edu',
  sharepointDomain: 'sharepoint.uccs.edu',
  intranetDomain: 'intranet.uccs.edu',
  trainingDomain: 'training.uccs.edu',
  mfaSystem: 'Duo',
  mfaApp: 'Duo Mobile',
  emailSystem: 'Microsoft 365',
  lms: 'Canvas',
  studentPortal: 'MyUCCS',

  location: 'Colorado Springs, CO',
  locationType: 'campus',

  enabledTasks: ['email', 'device'],
};

// ════════════════════════════════════════════════════════════════════════
// Registry
// ════════════════════════════════════════════════════════════════════════

export const ORG_CONFIGS: Record<string, OrgConfig> = {
  colostate: COLOSTATE,
  uccs: UCCS,
};

export const DEFAULT_ORG = 'uccs';

/**
 * Get org config from URL parameter or default.
 *
 * IMPORTANT: The ?org= URL parameter is only present on the initial page load.
 * Once React Router navigates internally (consent → instructions → email-task),
 * the query parameter is lost. So on first read, we persist to localStorage.
 * All subsequent reads use localStorage.
 */
export function getOrgConfig(): OrgConfig {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);

    // Persist ?task= parameter (for isolated Prolific runs like ?task=infosharing)
    const taskParam = params.get('task')?.toLowerCase();
    if (taskParam) {
      localStorage.setItem('selectedTask', taskParam);
    }

    // 1. Check URL parameter (highest priority — user just arrived)
    const orgParam = params.get('org')?.toLowerCase();
    if (orgParam && ORG_CONFIGS[orgParam]) {
      localStorage.setItem('selectedOrg', orgParam);
      return ORG_CONFIGS[orgParam];
    }

    // 2. Check localStorage (persists across internal navigation)
    const stored = localStorage.getItem('selectedOrg')?.toLowerCase();
    if (stored && ORG_CONFIGS[stored]) {
      return ORG_CONFIGS[stored];
    }
  }
  return ORG_CONFIGS[DEFAULT_ORG];
}
